import React from 'react';
import { Provider, ReactReduxContext, useSelector } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { LastLocationProvider } from 'react-router-last-location';
import { Route, Switch, Redirect } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { get } from 'lodash';
import { ConfigProvider } from 'antd';
import { StyleSheetManager } from 'styled-components';
import AnnotationModal from '@components/annotations/AnnotationModal';
import { PageNotFound, Retry } from '@components/common';
import { isCustomerServiceRoleSelector } from '@store/common/selectors';
import useCanAccessFeature from '@hooks/useCanAccessFeature';
import PulsePopupModal from '@components/pulse-announcement-modal';
import CheckActiveCustomer from '@components/common/CheckActiveCustomer';
import Feedback from '@components/feedback';
import {
  CURRENT_PRODUCT_MODE,
  isOdpModule,
  isAppModule,
  isEcoServerModule,
  PRODUCT_MODE_MAP,
  CHANGE_LOG_PATH,
  PRECISION_POLICY_PATH,
  PRECISION_RESILIENCY_PATH,
  MBR_POLICY_PATH,
} from '@utils/constants';
import { FilterManagement } from '@components/filter';
import DrilldownDashboard from '@src/pages/main/DrilldownDashboard';
import KPISettingsDrawer from '@components/kpi-settings-drawer';
import {
  Main,
  CustomDashboard,
  WithPersistence,
  WithModifyComparison,
  TrendsSharing,
  CompareSharing,
  Admin,
  AdvertisingInsightsDashboard,
  PrecisionPolicy,
  MbrPolicy,
  ChangeLog,
  MLSharingDashboard,
  MLSavedDashboard,
  Alert,
  WithResetTrends,
  SavedDashboards,
  Profile,
  ErrorConfig,
  TempNocDrilldownDashboard,
  OverviewSharingDashboard,
  OverviewSavedDashboard,
  VMSharingDashboard,
  VMSavedDashboard,
  AiAlertDiagnostics,
  AppAiAlertDiagnostics,
  PreAiAlertDiagnostics,
  PreAdAiAlertDiagnostics,
  PreAppAiAlertDiagnostics,
  TrendsSavedDashboard,
  UsageDashboardPage,
  Sandbox,
  Prompt,
  TempDashboard,
  AIRoadmapPage,
  AutoInsights,
  AppExperience,
  TempAppNocDrilldown,
} from '../pages';
import {
  AppExperienceDashboardPage,
  AppExperienceComparisonDrillDownPage,
  AppExperienceComparisonPage,
  AppExperienceTempDrilldownPage,
  AppTimelinePage,
  AppAiAlertsPage,
  AppWebhooksListPage,
  AppWebhooksEditPage,
  ServiceIntegrationsPage,
  NocPage,
  ExperienceDashboardPage,
  MetricLensPage,
  AiAlertsPage,
  TempDashboardNocRoute,
  DashboardNocRoute,
  NocSharingRoute,
  TempDashboardErrorDiagnosticsRoute,
  DashboardErrorDiagnosticsRoute,
  ErrorDiagnosticsSharingRoute,
  TempTimelineRoute,
  SavedTimelineRoute,
  TimelineSharingRoute,
  ViewerModulePage,
  ResiliencyPolicyPage,
  AppSankeyPage,
  AppFunnelPage,
} from './FeatureCheck';
import { useInitialization } from '../hooks';
import store, { history } from '../store';
import { SCOPE } from '../store/defaultLandingPage/types';
import theme from './localTheme';
import OdpRouter from './Odp';
import EcoServerRouter from './EcoServer';
import { EMPWidgetContainer } from '@components/common/EmpWidget';

import 'antd/dist/reset.css';
import '../styles/index.less';
import presetRenderer from './PresetDashboards';
import TempVdashDrilldownDashboard from '@src/pages/VdashDrillDown';

const logger = console;
const { baseUrl } = window.conviva;

export const homePagePath =
  [PRODUCT_MODE_MAP.AI, PRODUCT_MODE_MAP.APP, PRODUCT_MODE_MAP.ODP].includes(
    CURRENT_PRODUCT_MODE,
  ) || window.__POWERED_BY_QIANKUN__
    ? baseUrl
    : '/';
export const appPagePath = isAppModule || window.__POWERED_BY_QIANKUN__ ? baseUrl : '/';

export const singleAppPrefix = window.__POWERED_BY_QIANKUN__ ? '/app' : '';
const queryClient = new QueryClient();

const TempDashboardPage = (props) => <TempDashboard {...props} />;

logger.info(`build version: ${BUILD_VARS.VERSION}`);

const ErrorConfigPage = () => <ErrorConfig />;

const PromptPage = () => <Prompt />;

const RedirectToHomePage = () => {
  return (
    <ReactReduxContext.Consumer>
      {({ store }) => {
        const state = store.getState();
        const defaultPath = state[SCOPE].path;
        let actualPath = '';
        if (defaultPath) {
          if (window.__POWERED_BY_QIANKUN__) {
            actualPath = defaultPath.startsWith(singleAppPrefix)
              ? defaultPath
              : `${singleAppPrefix}${defaultPath}`;
          } else {
            actualPath = defaultPath.startsWith('/app/')
              ? defaultPath.split('/app')[1]
              : defaultPath;
          }
        } else {
          if (isAppModule) {
            actualPath = `${homePagePath}/dashboard/app-experience`;
          }
          if (isEcoServerModule) {
            actualPath = `${homePagePath}/dashboard/eco-server`;
          }
          if (isOdpModule) {
            // we will most likely be asked to change the landing route
            actualPath = `${homePagePath}/dashboard/explore`;
          } else {
            actualPath = `${homePagePath}/dashboard/trends`;
          }
        }
        return <Redirect to={actualPath} />;
      }}
    </ReactReduxContext.Consumer>
  );
};

const TrendsPage = (props) => {
  const canAccess = useCanAccessFeature({ feature: 'trends' });
  const isCustomerService = useSelector(isCustomerServiceRoleSelector);

  if (isAppModule) {
    return <Redirect to={`${baseUrl}/dashboard/app-experience`} />;
  }

  if (!canAccess) {
    // workaround for customer service role access restrictions
    if (isCustomerService) {
      return <Redirect to={`${baseUrl}/dashboard/viewer-module`} />;
    }
    return null;
  }

  return (
    <WithResetTrends>
      <WithPersistence>
        <Main {...props} />
      </WithPersistence>
    </WithResetTrends>
  );
};

const trendsRenderer = () => <TrendsPage />;
const ComparisonPage = () => (
  <WithModifyComparison>
    <Main />
  </WithModifyComparison>
);

const nocRenderer = (props) => <NocPage {...props} />;
const ovbRenderer = (props) => <ExperienceDashboardPage {...props} />;
const mlRenderer = (props) => <MetricLensPage {...props} />;
const aiAlertsRenderer = (props) => <AiAlertsPage {...props} />;
const aiAlertDiagnosticRenderer = (props) => <PreAiAlertDiagnostics {...props} />;
const adAiAlertDiagnosticRenderer = (props) => <PreAdAiAlertDiagnostics {...props} />;
// passing key to do a full remount
// to intiate a new jotai store to have clean state between multiple temp dashboards of same type
const aiAlertDiagnosticsDrilldown = (props) => {
  return <AiAlertDiagnostics key={get(props, 'match.params.id', -1)} {...props} />;
};

const tempTrendsDrilldown = (props) => {
  return <TempNocDrilldownDashboard key={get(props, 'match.params.id', -1)} {...props} />;
};

const tempVdashDrilldown = (props) => {
  return <TempVdashDrilldownDashboard key={get(props, 'match.params.id', -1)} {...props} />;
};

const AppTempTrendsDrilldown = (props) => {
  return <TempAppNocDrilldown key={get(props, 'match.params.id', -1)} {...props} />;
};

const trendsDrilldown = (props) => {
  return <DrilldownDashboard key={get(props, 'match.params.id', -1)} {...props} />;
};

const trendsSavedDashboardRenderer = (props) => {
  return <TrendsSavedDashboard key={get(props, 'match.params.id', -1)} {...props} />;
};

const trendsSharedDashboardRenderer = (props) => {
  return <TrendsSharing key={get(props, 'match.params.id', -1)} {...props} />;
};

const pageNotFoundRenderer = () => <PageNotFound />;
const AppExperienceRenderer = (props) => <AppExperienceDashboardPage {...props} />;
const AppComparisonPageRenderer = (props) => <AppExperienceComparisonDrillDownPage {...props} />;
const AppTempDrillDownPageRenderer = (props) => <AppExperienceTempDrilldownPage {...props} />;
const AppTimelineRenderer = (props) => <AppTimelinePage {...props} />;
const AppAiAlertRenderer = (props) => <AppAiAlertsPage {...props} />;
const AppWebhooksListRenderer = (props) => <AppWebhooksListPage {...props} />;
const AppAiAlertDiagnosticRenderer = (props) => <PreAppAiAlertDiagnostics {...props} />;
const AppAiAlertDiagnosticsDrilldown = (props) => {
  return <AppAiAlertDiagnostics key={get(props, 'match.params.id', -1)} {...props} />;
};

const AppCompareRenderer = (props) => <AppExperienceComparisonPage {...props} />;
const AppSankeyRenderer = (props) => <AppSankeyPage {...props} />;
const AppFunnelRenderer = (props) => <AppFunnelPage {...props} />;

const App = () => {
  const [initialized, error] = useInitialization(queryClient);
  if (!initialized) {
    return <Retry isLoading showError={error !== null} />;
  }

  // When application is switched in Apphub, history is not updated properly.
  // Following code will correct the history.
  if (window.location.pathname !== history.location.pathname) {
    history.replace(window.location.pathname);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <ConnectedRouter history={history}>
        <LastLocationProvider>
          <CheckActiveCustomer />
          {isAppModule && (
            <>
              <Switch>
                <Route exact path={homePagePath} render={RedirectToHomePage} />
                <Route
                  exact
                  path={`${appPagePath}/dashboard/app-experience`}
                  render={AppExperienceRenderer}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/app-experience/sharing/:id`}
                  render={trendsSharedDashboardRenderer}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/app-experience/saved/:id`}
                  render={trendsSavedDashboardRenderer}
                />
                <Route path={`${baseUrl}/dashboard/saved-dashboards`} component={SavedDashboards} />
                <Route path={`${baseUrl}/dashboard/compare/:id`} render={AppCompareRenderer} />
                <Route
                  exact
                  path={`${appPagePath}/dashboard/app-experience/modify-comparison`}
                  render={AppComparisonPageRenderer}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/app-experience/modify-real-time`}
                  component={AppExperience}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/app-experience/drilldown/:id`}
                  render={AppTempDrillDownPageRenderer}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/user-timeline`}
                  render={AppTimelineRenderer}
                />
                <Route exact path={`${baseUrl}/dashboard/sankey/:id`} render={AppSankeyRenderer} />
                <Route exact path={`${baseUrl}/dashboard/sankey`} render={AppSankeyRenderer} />
                <Route exact path={`${baseUrl}/dashboard/funnel`} render={AppFunnelRenderer} />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/user-timeline/:id`}
                  render={AppTimelineRenderer}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/user-timeline/:viewerId/temp/:id`}
                  render={AppTimelineRenderer}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/user-timeline/temp/:id`}
                  render={AppTimelineRenderer}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/app-experience/rt-drilldown/:id`}
                  render={AppTempTrendsDrilldown}
                />
                <Redirect
                  from={`${baseUrl}/dashboard/noc/:id`}
                  to={`${baseUrl}/dashboard/real-time/:id`}
                />
                <Route exact path={`${baseUrl}/dashboard/real-time`} render={nocRenderer} />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/real-time/:id`}
                  component={DashboardNocRoute}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/real-time/sharing/:id`}
                  component={NocSharingRoute}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/real-time/temp/:id`}
                  component={TempDashboardNocRoute}
                />
                <Route
                  exact
                  path={`${baseUrl}/dashboard/trends/drilldown/:id`}
                  render={trendsDrilldown}
                />
                <Route exact path={`${baseUrl}/dashboard/preset/:key`} render={presetRenderer} />

                <Route
                  path={`${appPagePath}/dashboard/ai-alerts/pre-app-diagnostics/:aiAlertId/:rootCauseParam`}
                  render={AppAiAlertDiagnosticRenderer}
                />
                <Route
                  path={`${appPagePath}/dashboard/ai-alerts/diagnostics/:id`}
                  render={AppAiAlertDiagnosticsDrilldown}
                />
                <Route
                  exact
                  path={`${appPagePath}/dashboard/ai-alerts`}
                  render={AppAiAlertRenderer}
                />
                <Route
                  exact
                  path={`${appPagePath}/settings/webhooks`}
                  render={AppWebhooksListRenderer}
                />
                <Route
                  path={`${appPagePath}/settings/webhooks/edit/:id`}
                  component={AppWebhooksEditPage}
                />
                <Route
                  path={`${appPagePath}/settings/webhooks/edit`}
                  component={AppWebhooksEditPage}
                />
                <Route exact path={`${appPagePath}/dashboard/prompt`} render={PromptPage} />
                <Route
                  exact
                  path={`${appPagePath}/dashboard/service-integrations`}
                  render={ServiceIntegrationsPage}
                />
                <Route render={pageNotFoundRenderer} />
              </Switch>
              <EMPWidgetContainer />
            </>
          )}
          {[PRODUCT_MODE_MAP.EI, PRODUCT_MODE_MAP.AI].includes(CURRENT_PRODUCT_MODE) && (
            <Switch>
              <Route exact path={homePagePath} render={RedirectToHomePage} />
              <Route exact path={`${baseUrl}/dashboard/trends`} render={trendsRenderer} />
              <Route exact path={`${baseUrl}/dashboard/prompt`} render={PromptPage} />
              <Route
                exact
                path={`${baseUrl}/dashboard/trends/modify-comparison`}
                render={ComparisonPage}
              />

              <Route exact path={`${baseUrl}/dashboard/preset/:key`} render={presetRenderer} />

              <Route
                exact
                path={`${baseUrl}/dashboard/trends/sharing/:id`}
                render={trendsSharedDashboardRenderer}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/trends/saved/:id`}
                render={trendsSavedDashboardRenderer}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/ai-alerts/diagnostics/sharing/:id`}
                render={trendsSharedDashboardRenderer}
              />
              <Route exact path={`${baseUrl}/dashboard/trends/:id`} component={Main} />
              <Route exact path={`${baseUrl}/dashboard/trends/modify-real-time`} render={Main} />
              <Route
                exact
                path={`${baseUrl}/dashboard/trends/rt-drilldown/:id`}
                render={tempTrendsDrilldown}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/trends/cd-drilldown/:id`}
                render={tempVdashDrilldown}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/trends/ba-drilldown/:id`}
                render={tempTrendsDrilldown}
              />

              <Route
                exact
                path={`${baseUrl}/dashboard/trends/ecoserver-drilldown/:id`}
                render={tempTrendsDrilldown}
              />

              <Route
                exact
                path={`${baseUrl}/dashboard/trends/na-drilldown/:id`}
                render={tempTrendsDrilldown}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/trends/drilldown/:id`}
                render={trendsDrilldown}
              />
              <Redirect
                from={`${baseUrl}/dashboard/noc/:id`}
                to={`${baseUrl}/dashboard/real-time/:id`}
              />
              <Redirect
                from={`${baseUrl}/dashboard/noc/sharing/:id`}
                to={`${baseUrl}/dashboard/real-time/sharing/:id`}
              />
              <Redirect
                from={`${baseUrl}/dashboard/noc/temp/:id`}
                to={`${baseUrl}/dashboard/real-time/temp/:id`}
              />
              <Redirect from={`${baseUrl}/dashboard/noc`} to={`${baseUrl}/dashboard/real-time`} />

              {/* for some customer may have saved new-real-time URL */}
              <Redirect
                from={`${baseUrl}/dashboard/new-real-time/:id`}
                to={`${baseUrl}/dashboard/real-time/:id`}
              />
              <Redirect
                from={`${baseUrl}/dashboard/new-real-time/sharing/:id`}
                to={`${baseUrl}/dashboard/real-time/sharing/:id`}
              />
              <Redirect
                from={`${baseUrl}/dashboard/new-real-time/temp/:id`}
                to={`${baseUrl}/dashboard/real-time/temp/:id`}
              />
              <Redirect
                from={`${baseUrl}/dashboard/new-real-time`}
                to={`${baseUrl}/dashboard/real-time`}
              />

              <Route exact path={`${baseUrl}/dashboard/real-time`} render={nocRenderer} />
              <Route
                exact
                path={`${baseUrl}/dashboard/real-time/:id`}
                component={DashboardNocRoute}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/real-time/sharing/:id`}
                component={NocSharingRoute}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/real-time/temp/:id`}
                component={TempDashboardNocRoute}
              />

              <Route
                path={`${baseUrl}/dashboard/metriclens/sharing/:id`}
                component={MLSharingDashboard}
              />
              <Route exact path={`${baseUrl}/dashboard/metriclens`} render={mlRenderer} />
              <Route path={`${baseUrl}/dashboard/metriclens/:id`} component={MLSavedDashboard} />

              <Route
                exact
                path={`${baseUrl}/dashboard/compare/sharing/:id`}
                component={CompareSharing}
              />
              <Route path={`${baseUrl}/dashboard/compare/:id`} component={CustomDashboard} />
              {/* Admin route will be depricated in a future release */}
              <Redirect
                from={`${singleAppPrefix}/admin/apps/*`}
                to={`${singleAppPrefix}/manage/apps/list`}
              />
              <Route path={`${singleAppPrefix}/manage`} component={Admin} />
              <Route
                path={`${singleAppPrefix}/advertising-insights/:id`}
                component={AdvertisingInsightsDashboard}
              />
              <Route exact path={`${PRECISION_POLICY_PATH}`} component={PrecisionPolicy} />
              <Route exact path={`${PRECISION_RESILIENCY_PATH}`} component={ResiliencyPolicyPage} />
              <Route path={`${CHANGE_LOG_PATH}`} component={ChangeLog} />
              <Route path={`${MBR_POLICY_PATH}`} component={MbrPolicy} />

              <Route
                exact
                path={`${baseUrl}/dashboard/error-diagnostics/:id`}
                component={DashboardErrorDiagnosticsRoute}
              />
              <Route
                path={`${baseUrl}/dashboard/error-diagnostics/sharing/:id`}
                component={ErrorDiagnosticsSharingRoute}
              />
              <Route
                path={`${baseUrl}/dashboard/error-diagnostics/temp/:id`}
                component={TempDashboardErrorDiagnosticsRoute}
              />

              <Route
                exact
                path={`${baseUrl}/dashboard/timeline/:id`}
                component={SavedTimelineRoute}
              />
              <Route
                path={`${baseUrl}/dashboard/timeline/temp/:id`}
                component={TempTimelineRoute}
              />
              <Route
                path={`${baseUrl}/dashboard/timeline/sharing/:id`}
                component={TimelineSharingRoute}
              />

              <Route
                exact
                path={`${baseUrl}/dashboard/viewer-module/:viewerId/sharing/:id`}
                component={VMSharingDashboard}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/viewer-module/saved/:id`}
                component={VMSavedDashboard}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/viewer-module`}
                component={ViewerModulePage}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/viewer-module/:viewerId`}
                component={ViewerModulePage}
              />
              <Route
                exact
                path={`${baseUrl}/dashboard/viewer-module/:viewerId/temp/:id`}
                component={ViewerModulePage}
              />

              <Route path={`${baseUrl}/alert`} component={Alert} />
              <Route path={`${baseUrl}/dashboard/saved-dashboards`} component={SavedDashboards} />
              <Route path={`${singleAppPrefix}/profile`} component={Profile} />
              <Route path={`${baseUrl}/dashboard/error-config`} component={ErrorConfigPage} />

              <Route
                path={`${baseUrl}/dashboard/overview/sharing/:id`}
                component={OverviewSharingDashboard}
              />
              <Route
                path={`${baseUrl}/dashboard/overview/:id`}
                component={OverviewSavedDashboard}
              />
              <Route path={`${baseUrl}/dashboard/overview`} render={ovbRenderer} />
              <Route
                path={`${baseUrl}/dashboard/service-integrations`}
                render={ServiceIntegrationsPage}
              />

              <Route
                path={`${baseUrl}/dashboard/ai-alerts/pre-diagnostics/:aiAlertId/:rootCauseParam`}
                render={aiAlertDiagnosticRenderer}
              />
              <Route
                path={`${baseUrl}/dashboard/ai-alerts/pre-ad-diagnostics/:aiAlertId/:rootCauseParam`}
                render={adAiAlertDiagnosticRenderer}
              />
              <Route
                path={`${baseUrl}/dashboard/ai-alerts/diagnostics/:id`}
                render={aiAlertDiagnosticsDrilldown}
              />
              <Route path={`${baseUrl}/dashboard/ai-alerts`} render={aiAlertsRenderer} />
              <Route path={`${baseUrl}/dashboard/temp/:type`} component={TempDashboardPage} />

              {/* <Route
                path={`${baseUrl}/admin/persistence`}
                component={PersistenceAdmin}
              /> */}

              <Route path={`${baseUrl}/sandbox`} component={Sandbox} />
              <Route
                path={`${baseUrl}/dashboard/ai-roadmap`}
                component={(props) => <AIRoadmapPage {...props} />}
              />

              <Route render={pageNotFoundRenderer} />
            </Switch>
          )}
          {isOdpModule && (
            <Switch>
              <Route path={`${baseUrl}`} component={OdpRouter} />
            </Switch>
          )}

          {isEcoServerModule && (
            <Switch>
              <Route path={`${baseUrl}`} component={EcoServerRouter} />
            </Switch>
          )}

          <Feedback />
          <AutoInsights />
          <AnnotationModal />
          <PulsePopupModal />
          <KPISettingsDrawer />
          <FilterManagement />
          <UsageDashboardPage />
        </LastLocationProvider>
      </ConnectedRouter>
    </QueryClientProvider>
  );
};

// disabling CSSOM to fix qiankun issues
// eslint-disable-next-line react/display-name
export default ({ container }: { container: HTMLElement }) => (
  <StyleSheetManager disableCSSOMInjection={true} target={container}>
    <Provider store={store as any}>
      <ConfigProvider
        theme={theme}
        // mount within the app space for dark mode
        getPopupContainer={(): any => document.getElementById('ifc-portals')}
      >
        <App />
      </ConfigProvider>
    </Provider>
  </StyleSheetManager>
);
